import React, { useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import DateFormatter from "../../../hooks/DateFormatter";
import { formatChangeLogValue } from "../../../Utils/changeLogValueFormat";
import { ChangesCell } from "./UndoChangeDetails";

/** Row and header height (fixed so the grid height can be worked out). */
const GRID_ROW_HEIGHT = 42;
/** Rows shown before the grid scrolls. */
const GRID_VISIBLE_ROWS = 6;

/**
 * The line items an undo changes, in the same grid style as the line item Change log: line item, change type, the
 * changed months (hover for month/day values), the yearly total now and after the undo, and when it was changed.
 * Used by both the "Undo last save" and the per-line undo dialogs.
 *
 * @param {Array} lines - [{ coaId, departmentId, coaName, changeType, changedOn, currentValue, valueAfterUndo, months }]
 */
const UndoLinesGrid = ({ lines }) => {
  const fitsWithoutScroll = lines.length <= GRID_VISIBLE_ROWS;

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Line item",
        colId: "lineItem",
        flex: 2,
        minWidth: 160,
        sortable: true,
        tooltipValueGetter: (params) => params.value,
        valueGetter: (params) => params.data?.coaName || `Line ${params.data?.coaId}`,
      },
      { headerName: "Change type", field: "changeType", flex: 1.4, minWidth: 130, sortable: true },
      {
        headerName: "Changes",
        colId: "changes",
        flex: 1.2,
        minWidth: 110,
        sortable: false,
        cellRenderer: ChangesCell,
      },
      {
        headerName: "Current value",
        field: "currentValue",
        flex: 1.2,
        minWidth: 120,
        sortable: true,
        valueFormatter: (params) => formatChangeLogValue(params.value),
      },
      {
        headerName: "After undo",
        field: "valueAfterUndo",
        flex: 1.2,
        minWidth: 120,
        sortable: true,
        valueFormatter: (params) => formatChangeLogValue(params.value),
      },
      {
        headerName: "Change date",
        field: "changedOn",
        flex: 1.5,
        minWidth: 170,
        sortable: true,
        valueFormatter: (params) =>
          params.value
            ? DateFormatter({ value: params.value, format: "do MMM yyyy, h:mm a", outputFormat: "string" })
            : "-",
      },
    ],
    []
  );

  return (
    <div
      className="ag-theme-quartz"
      style={{
        height: fitsWithoutScroll ? undefined : (GRID_VISIBLE_ROWS + 1) * GRID_ROW_HEIGHT,
        width: "100%",
        marginTop: "8px",
      }}
    >
      <AgGridReact
        rowData={lines}
        columnDefs={columnDefs}
        defaultColDef={{ resizable: true, suppressHeaderMenuButton: true }}
        domLayout={fitsWithoutScroll ? "autoHeight" : "normal"}
        rowHeight={GRID_ROW_HEIGHT}
        headerHeight={GRID_ROW_HEIGHT}
        getRowId={(params) => `${params.data.coaId}-${params.data.departmentId}`}
        suppressContextMenu={true}
        suppressDragLeaveHidesColumns={true}
      />
    </div>
  );
};

export default UndoLinesGrid;
